import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "../store";
import { clockActions } from "../store/clock-slice";
import { activeTabsActions } from "../store/tabs-slice";
import { Appointment } from "../types/appointment";
import { calculateTotalTime } from "../utils/calculateTotalTime";

import { handleServiceEvent } from "./../utils/handleServiceEvent";

export const useService = (appointment: Appointment) => {
  const dispatch = useDispatch();

  const user = useSelector((state: RootState) => state.user.currentUser.user);

  const clockodoUser = useSelector(
    (state: RootState) => state.user.clockodoUser
  );

  const [loading, setLoading] = useState(false);

  const startService = async (): Promise<void> => {
    if (!appointment.id || !user?.id) {
      return;
    }

    setLoading(true);

    try {
      await handleServiceEvent(appointment, "start", clockodoUser, user);

      const { totalTime, lastEvent, isRunning, isPaused } =
        await calculateTotalTime(appointment.id, user.id);

      dispatch(
        clockActions.setClock({
          isRunning,
          appointmentId: appointment.id,
          employeeId: user.id,
          currentTime: totalTime,
          initialTime: totalTime,
          isPaused,
          lastEventTimestamp: lastEvent?.eventTimestamp ?? null,
        })
      );
    } catch (error) {
      console.log("Some error happened", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const stopService = async (): Promise<void> => {
    if (!appointment.id || !user?.id) {
      return;
    }

    setLoading(true);

    try {
      await handleServiceEvent(appointment, "stop", clockodoUser, user);

      dispatch(clockActions.resetClock(appointment.id));
      dispatch(activeTabsActions.setMonitorsStep(1));

    } catch (error) {
      console.log("Some error happened", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const pauseService = async (): Promise<void> => {
    if (!appointment.id || !user?.id) return;

    setLoading(true);

    try {
      await handleServiceEvent(appointment, "pause", clockodoUser, user);

      const { totalTime, lastEvent, isRunning, isPaused } =
        await calculateTotalTime(appointment.id, user.id);

      dispatch(
        clockActions.setClock({
          appointmentId: appointment.id,
          employeeId: user.id,
          currentTime: totalTime,
          initialTime: totalTime,
          isRunning,
          isPaused,
          lastEventTimestamp: lastEvent?.eventTimestamp ?? null,
        })
      );
    } catch (error) {
      console.log("Some error happened", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const resumeService = async (): Promise<void> => {
    if (!appointment.id || !user?.id) return;

    setLoading(true);

    try {
      await handleServiceEvent(appointment, "resume", clockodoUser, user);

      const { totalTime, isRunning, isPaused, lastEvent } =
        await calculateTotalTime(appointment.id, user.id);

      dispatch(
        clockActions.setClock({
          appointmentId: appointment.id,
          employeeId: user.id,
          currentTime: totalTime,
          initialTime: totalTime,
          isRunning,
          isPaused,
          lastEventTimestamp: lastEvent?.eventTimestamp ?? null,
        })
      );
    } catch (error) {
      console.log("Some error happened", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    startService,
    stopService,
    pauseService,
    resumeService,
  };
};
